import { Button } from "@faire/slate/components/button";
import { IResponsiveSDUIText } from "@faire/web-api--source/indigofair/data/IResponsiveSDUIText";
import { ISDUIText } from "@faire/web-api--source/indigofair/data/ISDUIText";
import { TextAccessibilityAttribute } from "@faire/web-api--source/indigofair/data/TextAccessibilityAttribute";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import React from "react";

import {
  accessibilityTextTagMap,
  getTypographyVariant,
  mapSDUITextToPropsArray,
} from "./private/utils";

interface IProps {
  sduiText: ISDUIText | IResponsiveSDUIText | undefined;
}

export const SDUIText: React.FC<IProps> = ({ sduiText }) => {
  const maybeResponsiveSDUIText = sduiText as IResponsiveSDUIText;
  const responsiveSDUIText = useResponsiveValue<ISDUIText | undefined>({
    mobileAndAbove: maybeResponsiveSDUIText?.mobile_and_above,
    tabletAndAbove: maybeResponsiveSDUIText?.tablet_and_above,
    desktopAndAbove: maybeResponsiveSDUIText?.desktop_and_above,
  });

  return (
    <SDUITextBase sduiText={responsiveSDUIText ?? (sduiText as ISDUIText)} />
  );
};

const SDUITextBase: React.FC<{ sduiText?: ISDUIText }> = ({ sduiText }) => {
  if (!sduiText) {
    return null;
  }

  const textPropsArray = mapSDUITextToPropsArray(sduiText);

  const baseTextVariant = getTypographyVariant(
    sduiText.slate_text_segments[0]?.type ??
      sduiText.slate_text_segments[0]?.slate_text?.style?.type ??
      TextStyleType.PARAGRAPH,
    undefined
  );

  const textTag =
    accessibilityTextTagMap[
      sduiText.text_accessibility_attribute ??
        TextAccessibilityAttribute.TEXT_ACCESSIBILITY_ATTRIBUTE_UNKNOWN
    ] ?? undefined;

  return (
    <Typography variant={baseTextVariant} as={textTag}>
      {textPropsArray.map((textProps, index) => {
        const key = `${textProps.id}_${index}`;
        const isLast = index === textPropsArray.length - 1;
        const component =
          textProps.variant === "plain" ? (
            <Button
              {...(textProps as React.ComponentProps<typeof Button>)}
              key={key}
            />
          ) : (
            <Span
              {...(textProps as React.ComponentProps<typeof Typography>)}
              key={key}
            />
          );
        return isLast ? (
          component
        ) : (
          <React.Fragment key={`${key}-fragment`}>{component} </React.Fragment>
        );
      })}
    </Typography>
  );
};
